import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createLazyIcon } from "../../lib/lazy-icons";
import {
  generateDatesFromRule,
  RecurrenceError,
  type RecurrenceFreq,
  type RecurrenceRule,
  MAX_RECURRING_OCCURRENCES,
} from "../../lib/recurrence";

const Repeat = createLazyIcon("Repeat");
const Lock = createLazyIcon("Lock");

export interface RecurrenceConfig {
  rule: RecurrenceRule;
  /** null = no reminders */
  reminderDaysBefore: number | null;
}

interface RecurrenceSectionProps {
  value: RecurrenceConfig | null;
  onChange: (value: RecurrenceConfig | null) => void;
  hasPaidPlan: boolean;
  isAuthenticated: boolean;
  /** Count of dates currently picked in the calendar (custom mode preview). */
  selectedDatesCount: number;
}

const FREQUENCIES: RecurrenceFreq[] = [
  "weekly",
  "biweekly",
  "monthly",
  "daily",
  "custom",
];

const REMINDER_CHOICES = [1, 2, 3, null] as const;

/**
 * Recurring poll configuration section (SOLO feature).
 *
 * Non-custom frequencies generate the occurrence dates from dtstart/until;
 * the parent injects them into the calendar state. "custom" leaves the
 * calendar as the source of dates.
 */
export function RecurrenceSection({
  value,
  onChange,
  hasPaidPlan,
  isAuthenticated,
  selectedDatesCount,
}: RecurrenceSectionProps) {
  const { t, i18n } = useTranslation(["creator"]);
  const enabled = value !== null;
  const rule = value?.rule;

  const preview = useMemo(() => {
    if (!rule || rule.freq === "custom") return null;
    if (!rule.dtstart || !rule.until) return null;
    try {
      const dates = generateDatesFromRule(rule);
      return { count: dates.length, error: null as string | null };
    } catch (err) {
      if (err instanceof RecurrenceError) {
        const key =
          err.code === "TOO_MANY_OCCURRENCES"
            ? "recurrence.error_too_many"
            : err.code === "TOO_FEW_OCCURRENCES"
              ? "recurrence.error_too_few"
              : "recurrence.error_invalid";
        return {
          count: 0,
          error: t(`creator:${key}`, { max: MAX_RECURRING_OCCURRENCES }),
        };
      }
      return { count: 0, error: t("creator:recurrence.error_invalid") };
    }
  }, [rule, t]);

  const setRule = (patch: Partial<RecurrenceRule>) => {
    if (!value) return;
    onChange({ ...value, rule: { ...value.rule, ...patch } });
  };

  const toggle = () => {
    if (enabled) {
      onChange(null);
    } else {
      onChange({
        rule: { freq: "weekly", dtstart: "", until: "" },
        reminderDaysBefore: 1,
      });
    }
  };

  // Locked state: feature needs an authenticated SOLO account.
  const locked = !isAuthenticated || !hasPaidPlan;

  return (
    <div
      className="border border-gray-700 rounded-lg bg-[#1e1e1e] p-4 space-y-3"
      data-testid="recurrence-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-200">
          <Repeat className="w-4 h-4" />
          <span className="text-sm font-medium">
            {t("creator:recurrence.title")}
          </span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/40">
            {t("creator:recurrence.solo_badge")}
          </span>
        </div>

        {locked ? (
          <Link
            to="/pricing"
            className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
            data-testid="recurrence-upgrade-link"
          >
            <Lock className="w-3.5 h-3.5" />
            {t("creator:recurrence.upgrade_cta")}
          </Link>
        ) : (
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={t("creator:recurrence.title")}
            onClick={toggle}
            data-testid="recurrence-toggle"
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              enabled ? "bg-blue-500" : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                enabled ? "translate-x-[18px]" : "translate-x-[3px]"
              }`}
            />
          </button>
        )}
      </div>

      {locked && (
        <p className="text-xs text-gray-400">
          {t("creator:recurrence.locked_description")}
        </p>
      )}

      {enabled && value && (
        <div className="space-y-3" data-testid="recurrence-config">
          {/* Frequency */}
          <div className="flex flex-wrap gap-2">
            {FREQUENCIES.map((freq) => (
              <button
                key={freq}
                type="button"
                onClick={() => setRule({ freq })}
                data-testid={`recurrence-freq-${freq}`}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  value.rule.freq === freq
                    ? "border-blue-500 bg-blue-500/15 text-blue-300"
                    : "border-gray-700 text-gray-300 hover:border-blue-400"
                }`}
              >
                {t(`creator:recurrence.freq_${freq}`)}
              </button>
            ))}
          </div>

          {value.rule.freq === "custom" ? (
            <p className="text-xs text-gray-400">
              {t("creator:recurrence.custom_hint", {
                count: selectedDatesCount,
              })}
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-gray-400 flex flex-col gap-1">
                {t("creator:recurrence.from")}
                <input
                  type="date"
                  value={value.rule.dtstart ?? ""}
                  onChange={(e) => setRule({ dtstart: e.target.value })}
                  data-testid="recurrence-dtstart"
                  className="bg-[#141414] border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-200 [color-scheme:dark]"
                />
              </label>
              <label className="text-xs text-gray-400 flex flex-col gap-1">
                {t("creator:recurrence.until")}
                <input
                  type="date"
                  value={value.rule.until ?? ""}
                  min={value.rule.dtstart || undefined}
                  onChange={(e) => setRule({ until: e.target.value })}
                  data-testid="recurrence-until"
                  className="bg-[#141414] border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-200 [color-scheme:dark]"
                />
              </label>
              {preview &&
                (preview.error ? (
                  <p
                    className="text-xs text-red-400"
                    data-testid="recurrence-error"
                  >
                    {preview.error}
                  </p>
                ) : (
                  <p
                    className="text-xs text-green-400"
                    data-testid="recurrence-preview"
                  >
                    {t("creator:recurrence.preview_count", {
                      count: preview.count,
                    })}
                  </p>
                ))}
            </div>
          )}

          {/* Reminders */}
          <div className="space-y-1.5">
            <p className="text-xs text-gray-400">
              {t("creator:recurrence.reminder_label")}
            </p>
            <div className="flex flex-wrap gap-2">
              {REMINDER_CHOICES.map((days) => (
                <button
                  key={days ?? "never"}
                  type="button"
                  onClick={() =>
                    onChange({ ...value, reminderDaysBefore: days })
                  }
                  data-testid={`recurrence-reminder-${days ?? "never"}`}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                    value.reminderDaysBefore === days
                      ? "border-blue-500 bg-blue-500/15 text-blue-300"
                      : "border-gray-700 text-gray-300 hover:border-blue-400"
                  }`}
                >
                  {days === null
                    ? t("creator:recurrence.reminder_never")
                    : t("creator:recurrence.reminder_days", { count: days })}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500">
              {t("creator:recurrence.reminder_hint", {
                timezone: Intl.DateTimeFormat(i18n.language).resolvedOptions()
                  .timeZone,
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecurrenceSection;
